import moment, { Moment } from "moment";
import { SEARCH_FIELDS } from "../../../mappings";
import { isDateField } from "../../../util";
import { FlowState, QueryGroup, QueryItem, SuggestionItem } from "../@types";
import {
  ALL_SUGGESTIONS,
  CATEGORICAL_SEARCH_DATA,
  FLOW_STATES
} from "../consts";

import { TimeEnum } from "../../../../../lib/enums";
import { Suggestion } from "../../../@types/SearchAutosuggest";
import { ISO_FORMAT_WITHOUT_MILLIS } from "../../../consts";
import { TIME_RANGE_SUGGESTIONS } from "../../consts";
import AutosuggestStringLabel from "../components/AutosuggestStringLabel";
import AutosuggestSuggestionLabel from "../components/AutosuggestSuggestionLabel";
import { createNewOption } from "./createNewOptionsUtils";

const getNumberOfBracketsAndCurrentGroupIndex = (
  allGroups: readonly QueryGroup[],
  currentGroupKey?: string
) => {
  let currentGroupIndex = -1;
  let numberOfOpenedBrackets = 0;
  let numberOfClosedBrackets = 0;
  allGroups.forEach((item, index) => {
    if (currentGroupIndex !== -1 && index > currentGroupIndex) return;

    if (item.itemKey === currentGroupKey) {
      currentGroupIndex = index;
      return;
    }

    if (item.value === "(") numberOfOpenedBrackets++;
    if (item.value === ")") numberOfClosedBrackets++;
  });

  return { numberOfOpenedBrackets, numberOfClosedBrackets, currentGroupIndex };
};

const getPreviousGroupInformation = (
  allGroups: readonly QueryGroup[],
  previousGroupIndex: number
) => {
  const previousGroup = allGroups[previousGroupIndex];
  const previousGroupIsOperator = ["AND", "OR"].includes(
    previousGroup?.value ?? ""
  );
  const previousGroupIsNot = previousGroup?.value === "NOT";
  const previousGroupIsOpenBracket = previousGroup?.value === "(";

  return {
    previousGroup,
    previousGroupIndex,
    previousGroupIsOperator,
    previousGroupIsNot,
    previousGroupIsOpenBracket
  };
};

const getSuggestionsForAllKeywords = (
  allGroups: readonly QueryGroup[],
  currentGroup?: QueryGroup,
  currentGroupKey?: string
) => {
  const { numberOfOpenedBrackets, numberOfClosedBrackets, currentGroupIndex } =
    getNumberOfBracketsAndCurrentGroupIndex(allGroups, currentGroupKey);
  const shouldShowClosedBracket =
    numberOfOpenedBrackets > numberOfClosedBrackets;

  const previousGroupIndex =
    currentGroup && currentGroupKey && currentGroupIndex > -1
      ? currentGroupIndex - 1
      : allGroups.length - 1;
  const {
    previousGroupIsOperator,
    previousGroupIsNot,
    previousGroupIsOpenBracket
  } = getPreviousGroupInformation(allGroups, previousGroupIndex);

  return [
    ...(previousGroupIsOperator ||
    previousGroupIndex < 0 ||
    previousGroupIsNot ||
    previousGroupIsOpenBracket
      ? []
      : ["AND", "OR"]),
    ...(previousGroupIsNot ? [] : ["NOT"]),
    "(",
    ...(shouldShowClosedBracket &&
    !previousGroupIsNot &&
    !previousGroupIsOperator
      ? [")"]
      : []),
    ...Object.values(SEARCH_FIELDS).sort()
  ].map(field => ({
    value: field,
    label: field
  }));
};

const getSuggestionsForTimeRange = () => {
  const TIMERANGE_MAP: Record<string, Moment> = {
    [TimeEnum.TODAY]: moment(),
    [TimeEnum.LAST_WEEK]: moment().subtract(1, "weeks"),
    [TimeEnum.LAST_MONTH]: moment().subtract(1, "months"),
    [TimeEnum.LAST_QUARTER]: moment().subtract(3, "months"),
    [TimeEnum.LAST_YEAR]: moment().subtract(1, "years")
  };
  const timerangeEndDay = moment()
    .utc()
    .endOf("day")
    .format(ISO_FORMAT_WITHOUT_MILLIS);

  return TIME_RANGE_SUGGESTIONS.map(value => {
    const timerangeStartDay = TIMERANGE_MAP[value.value]
      ? TIMERANGE_MAP[value.value]
          .utc()
          .startOf("day")
          .format(ISO_FORMAT_WITHOUT_MILLIS)
      : "";

    return {
      value:
        value.value === "__datepicker" ? `custom__datepicker` : value.value,
      label: value.name,
      complexValue:
        value.value === "__datepicker"
          ? undefined
          : [timerangeStartDay, "TO", timerangeEndDay, "]"]
    };
  });
};

const getSuggestionsForKeywordValues = (
  keyword: string,
  suggestion: Suggestion,
  flowState: FlowState
) => {
  if (CATEGORICAL_SEARCH_DATA[keyword]) {
    return CATEGORICAL_SEARCH_DATA[keyword].map(value => ({
      value: value.name,
      complexValue: value.value.split(","),
      label: value.name
    }));
  }

  if (
    isDateField(keyword) &&
    [FLOW_STATES.SINGLE_OR_MULTI_VALUE, FLOW_STATES.FIRST_MULTI_VALUE].includes(
      flowState
    )
  ) {
    return getSuggestionsForTimeRange();
  }

  return (ALL_SUGGESTIONS[keyword].values || []).map(value => {
    const parsedValue = value.includes(" ") ? `"${value}"` : value;
    return {
      value: parsedValue,
      label: parsedValue
    };
  });
};

export const getSuggestionsBasedOnState = ({
  flowState,
  currentGroup,
  currentGroupKey,
  allGroups
}: {
  flowState: FlowState;
  currentGroup?: QueryGroup;
  currentGroupKey?: string;
  allGroups: readonly QueryGroup[];
}): Array<SuggestionItem> => {
  if (flowState === FLOW_STATES.ALL_KEYWORDS)
    return getSuggestionsForAllKeywords(
      allGroups,
      currentGroup,
      currentGroupKey
    );

  const keyword =
    currentGroup?.grouped?.[0]?.value ?? allGroups?.at(-1)?.keyword;
  const operatorSuggestions: Array<SuggestionItem> = [];
  const keywordValuesSuggestions: Array<SuggestionItem> =
    keyword && ALL_SUGGESTIONS[keyword]
      ? getSuggestionsForKeywordValues(
          keyword,
          ALL_SUGGESTIONS[keyword],
          flowState
        )
      : [];

  const isSingleValueAndSupportsRangeOrMultiValue =
    flowState === FLOW_STATES.SINGLE_OR_MULTI_VALUE &&
    keyword &&
    ALL_SUGGESTIONS[keyword] &&
    (ALL_SUGGESTIONS[keyword].operators?.IN ||
      ALL_SUGGESTIONS[keyword].operators?.RANGE);
  if (isSingleValueAndSupportsRangeOrMultiValue)
    operatorSuggestions.push({ value: "[", label: "[" });

  if (
    [
      FLOW_STATES.MULTI_VALUE_OR_RANGE,
      FLOW_STATES.FIRST_MULTI_VALUE,
      FLOW_STATES.MULTI_VALUE
    ].includes(flowState)
  )
    operatorSuggestions.push({ value: "]", label: "]" });

  const isMultiValueAndSupportsRange =
    flowState === FLOW_STATES.MULTI_VALUE_OR_RANGE &&
    keyword &&
    ALL_SUGGESTIONS[keyword] &&
    ALL_SUGGESTIONS[keyword].operators?.RANGE;
  if (isMultiValueAndSupportsRange)
    operatorSuggestions.push({ value: "TO", label: "TO" });

  if (flowState === FLOW_STATES.CLOSE_RANGE)
    operatorSuggestions.push({ value: "]", label: "]" });

  return [...operatorSuggestions, ...keywordValuesSuggestions];
};

export const convertSuggestionToOption = (
  inputValue: string,
  flowState: FlowState,
  suggestion: SuggestionItem,
  shouldBold?: boolean
): QueryItem =>
  createNewOption({
    value: suggestion.value,
    label:
      ALL_SUGGESTIONS[suggestion.value] &&
      flowState === FLOW_STATES.ALL_KEYWORDS ? (
        <AutosuggestSuggestionLabel
          suggestion={ALL_SUGGESTIONS[suggestion.value]}
          inputValue={inputValue}
        />
      ) : (
        <AutosuggestStringLabel
          suggestion={suggestion.label}
          inputValue={inputValue}
          shouldBold={shouldBold}
        />
      ),
    flowState,
    complexValue: suggestion.complexValue,
    useValueAsItemKey: true
  });

export const convertSuggestionsToOptions = (
  inputValue: string,
  flowState: FlowState,
  suggestions: SuggestionItem[],
  shouldBold?: boolean
): Array<QueryItem> =>
  suggestions.map(suggestion =>
    convertSuggestionToOption(inputValue, flowState, suggestion, shouldBold)
  );
